if (!customElements.get('location-selector')) {
  const LOCATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const GEO_RETRY_AFTER_ERROR_MS = 6 * 60 * 60 * 1000;
  const GEO_RETRY_AFTER_DENIED_MS = 7 * 24 * 60 * 60 * 1000;

  class LocationSelector extends HTMLElement {
    constructor() {
      super();
      this.storageKey = this.dataset.storageKey || 'tilemall-location';
      this.metaKey = `${this.storageKey}-meta`;
      this.toggleButton = this.querySelector('.header-location-selector__toggle');
      this.panel = this.querySelector('.js-location-panel');
      this.line1El = this.querySelector('.js-location-line1');
      this.line2El = this.querySelector('.js-location-line2');
      this.inputEl = this.querySelector('.js-location-input');
      this.listEl = this.querySelector('.js-location-list');
      this.loadingEl = this.querySelector('.js-location-loading');
      this.notFoundEl = this.querySelector('.js-location-not-found');
      this.clearButton = this.querySelector('.js-location-clear');
      this.closeButton = this.querySelector('.js-location-close');
      this.debounceQuery = this.debounce(this.querySuburbs.bind(this), 450);

      this.restoreFromStorage();
      this.bindEvents();

      this.maybeAutoDetectLocation();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.outsideClickHandler);
      document.removeEventListener('keyup', this.keyupHandler);
      window.removeEventListener('tilemall:location:change', this.locationChangeHandler);
    }

    bindEvents() {
      this.toggleButton.addEventListener('click', this.handleToggleClick.bind(this));
      this.inputEl.addEventListener('input', this.handleInput.bind(this));
      this.listEl.addEventListener('click', this.handleSelect.bind(this));
      this.clearButton.addEventListener('click', this.handleClear.bind(this));
      this.closeButton.addEventListener('click', this.close.bind(this));

      this.outsideClickHandler = this.outsideClickHandler || this.handleOutsideClick.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.locationChangeHandler = this.locationChangeHandler || this.handleLocationChange.bind(this);

      window.addEventListener('tilemall:location:change', this.locationChangeHandler);
    }

    storageGet() {
      try {
        if (window.theme?.storageUtil?.get) return window.theme.storageUtil.get(this.storageKey, true);
      } catch (e) {
        // Ignore storage errors.
      }
      try {
        return JSON.parse(localStorage.getItem(this.storageKey));
      } catch (e) {
        return null;
      }
    }

    storageSet(value) {
      try {
        if (window.theme?.storageUtil?.set) return window.theme.storageUtil.set(this.storageKey, value);
      } catch (e) {
        // Ignore storage errors.
      }
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(value));
      } catch (e) {
        // Ignore storage errors.
      }
      return null;
    }

    metaGet() {
      try {
        if (window.theme?.storageUtil?.get) return window.theme.storageUtil.get(this.metaKey, true);
      } catch (e) {
        // Ignore storage errors.
      }
      try {
        return JSON.parse(localStorage.getItem(this.metaKey));
      } catch (e) {
        return null;
      }
    }

    metaSet(value) {
      try {
        if (window.theme?.storageUtil?.set) return window.theme.storageUtil.set(this.metaKey, value);
      } catch (e) {
        // Ignore storage errors.
      }
      try {
        localStorage.setItem(this.metaKey, JSON.stringify(value));
      } catch (e) {
        // Ignore storage errors.
      }
      return null;
    }

    storageRemove() {
      try {
        if (window.theme?.storageUtil?.get && window.theme?.storageUtil?.set) {
          localStorage.removeItem(`cc-${this.storageKey}`);
          return null;
        }
      } catch (e) {
        // Ignore storage errors.
      }
      try {
        localStorage.removeItem(this.storageKey);
      } catch (e) {
        // Ignore storage errors.
      }
      return null;
    }

    restoreFromStorage() {
      const location = this.storageGet();
      this.updateButtonLabel(location);
    }

    updateButtonLabel(location) {
      const defaultLine1 = (this.line1El?.dataset?.defaultLine1 || '').trim();
      const defaultLine2 = (this.line2El?.dataset?.defaultLine2 || '').trim();
      if (!location?.postcode || !location?.state) {
        if (defaultLine1) {
          this.line1El.textContent = defaultLine1;
          this.line1El.classList.remove('hidden');
        } else {
          this.line1El.classList.add('hidden');
          this.line1El.textContent = '';
        }

        this.line2El.textContent = defaultLine2;
        return;
      }

      this.line1El.textContent = `${location.postcode} - ${location.state}`;
      this.line1El.classList.remove('hidden');

      const suburb = (location.suburb || '').trim();
      this.line2El.textContent = suburb || defaultLine2;
    }

    open() {
      this.classList.add('is-open');
      this.panel.hidden = false;
      this.toggleButton.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.outsideClickHandler);
      document.addEventListener('keyup', this.keyupHandler);

      const existing = this.storageGet();
      if (existing?.postcode) this.inputEl.value = existing.postcode;
      this.inputEl.focus();
      this.inputEl.select();
    }

    close() {
      this.classList.remove('is-open');
      this.panel.hidden = true;
      this.toggleButton.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.outsideClickHandler);
      document.removeEventListener('keyup', this.keyupHandler);
      this.clearResults();
    }

    handleToggleClick(evt) {
      evt.preventDefault();
      if (this.classList.contains('is-open')) this.close();
      else this.open();
    }

    handleOutsideClick(evt) {
      if (!this.classList.contains('is-open')) return;
      if (evt.target.closest('location-selector') === this) return;
      this.close();
    }

    handleKeyup(evt) {
      if (evt.key === 'Escape') this.close();
    }

    handleLocationChange(evt) {
      const location = evt?.detail?.location;
      this.updateButtonLabel(location);
    }

    sanitizePostcode(value) {
      return (value || '').toString().replace(/\D/g, '').slice(0, 4);
    }

    isLocationFresh(location) {
      if (!location?.postcode) return false;
      if (!location?.expiresAt) return true;
      return Date.now() < Number(location.expiresAt);
    }

    async maybeAutoDetectLocation() {
      if (this.dataset.enableAutoGeo !== 'true') return;
      if (window.__tilemallLocationGeoInit) return;
      window.__tilemallLocationGeoInit = true;

      const existing = this.storageGet();
      if (this.isLocationFresh(existing)) return;

      const meta = this.metaGet() || {};
      const now = Date.now();
      if (meta.nextGeoAttemptAt && now < meta.nextGeoAttemptAt) return;

      if (!('geolocation' in navigator)) return;

      let permissionState = 'prompt';
      if (navigator.permissions?.query) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
          permissionState = permissionStatus?.state || permissionState;
        } catch (e) {
          // Ignore.
        }
      }

      if (permissionState === 'denied') {
        this.metaSet({ ...meta, nextGeoAttemptAt: now + GEO_RETRY_AFTER_DENIED_MS });
        return;
      }

      await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              const { latitude, longitude } = pos.coords || {};
              if (typeof latitude !== 'number' || typeof longitude !== 'number') return resolve();

              const data = await this.querySuburbsByLocation(latitude, longitude);
              const location = this.parseLocationFromByLocationResponse(data);
              if (!location?.postcode) return resolve();

              const payload = {
                ...location,
                source: 'geo',
                updatedAt: now,
                expiresAt: now + LOCATION_TTL_MS
              };

              this.storageSet(payload);
              this.updateButtonLabel(payload);
              window.dispatchEvent(new CustomEvent('tilemall:location:change', { detail: { location: payload } }));

              this.metaSet({ ...meta, nextGeoAttemptAt: now + LOCATION_TTL_MS });
            } catch (e) {
              this.metaSet({ ...meta, nextGeoAttemptAt: now + GEO_RETRY_AFTER_ERROR_MS });
            } finally {
              resolve();
            }
          },
          (err) => {
            const retryMs = err?.code === 1 ? GEO_RETRY_AFTER_DENIED_MS : GEO_RETRY_AFTER_ERROR_MS;
            this.metaSet({ ...meta, nextGeoAttemptAt: now + retryMs });
            resolve();
          },
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
        );
      });
    }

    parseLocationFromByLocationResponse(data) {
      if (!data) return null;

      const postcode =
        data.postcode
        || data.post_code
        || data.Postcode
        || data.PostCode;
      const suburb = data.suburb || data.Suburb;
      const state = data.state || data.State;

      if (postcode && state) return { postcode: `${postcode}`, suburb, state };

      const suburbs = data.suburbs || data.Suburbs || data.results;
      if (Array.isArray(suburbs) && suburbs.length) {
        const first = suburbs[0];
        const p = first.Postcode || first.PostCode || first.postcode || first.post_code;
        const s = first.Suburb || first.suburb;
        const st = first.State || first.state;
        if (p && st) return { postcode: `${p}`, suburb: s, state: st };
      }

      return null;
    }

    handleInput(evt) {
      const postcode = this.sanitizePostcode(evt.target.value);
      if (postcode !== evt.target.value) evt.target.value = postcode;

      this.loadingEl.classList.remove('hidden');
      this.notFoundEl.classList.add('hidden');
      this.clearResults();

      if (postcode.length < 2) {
        this.loadingEl.classList.add('hidden');
        return;
      }

      this.debounceQuery(postcode)
        .then((res) => this.buildOptions(postcode, res?.suburbs ?? []))
        .finally(() => {
          this.loadingEl.classList.add('hidden');
        });
    }

    clearResults() {
      this.listEl.innerHTML = '';
    }

    buildOptions(postcode, suburbs) {
      this.clearResults();

      suburbs.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'header-location-selector__option';
        li.dataset.postcode = postcode;
        li.dataset.suburb = item.Suburb;
        li.dataset.state = item.State;
        li.setAttribute('role', 'option');
        const suburbEl = document.createElement('span');
        suburbEl.className = 'header-location-selector__option-suburb';
        suburbEl.textContent = item.Suburb;

        const stateEl = document.createElement('span');
        stateEl.className = 'header-location-selector__option-state';
        stateEl.textContent = item.State;

        li.append(suburbEl, stateEl);
        this.listEl.appendChild(li);
      });

      if (!suburbs.length) {
        this.notFoundEl.classList.remove('hidden');
      }
    }

    handleSelect(evt) {
      const option = evt.target.closest('.header-location-selector__option');
      if (!option) return;

      const now = Date.now();
      const location = {
        postcode: option.dataset.postcode,
        suburb: option.dataset.suburb,
        state: option.dataset.state,
        source: 'manual',
        updatedAt: now,
        expiresAt: now + LOCATION_TTL_MS
      };

      this.storageSet(location);
      this.updateButtonLabel(location);

      window.dispatchEvent(new CustomEvent('tilemall:location:change', { detail: { location } }));
      this.close();
    }

    handleClear(evt) {
      evt.preventDefault();
      this.storageRemove();
      const meta = this.metaGet() || {};
      this.metaSet({ ...meta, nextGeoAttemptAt: Date.now() + GEO_RETRY_AFTER_DENIED_MS });
      const location = null;
      this.updateButtonLabel(location);
      window.dispatchEvent(new CustomEvent('tilemall:location:change', { detail: { location } }));
      this.close();
    }

    async querySuburbs(postCode) {
      const response = await fetch(`/apps/tilemall/suburbs?post_code=${postCode}`);
      return response.json();
    }

    async querySuburbsByLocation(lat, lng) {
      const response = await fetch(`/apps/tilemall/suburbs-by-location?lat=${lat}&lng=${lng}`);
      return response.json();
    }

    debounce(fn, timeout = 300) {
      let timer;
      return (...args) => new Promise((resolve) => {
        clearTimeout(timer);
        timer = setTimeout(async () => resolve(await fn(...args)), timeout);
      });
    }
  }

  customElements.define('location-selector', LocationSelector);
}
